import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useEffect, useState } from 'react'
import { API_BASE } from '../config/api'

interface ApiProject {
  id: string
  title: string
  category?: string
  description: string
  image?: string
  technologies: string[]
  features: string[]
  status?: string
  duration?: string
  team?: string
  results?: string[]
  gallery?: string[]
  liveUrl?: string
  githubUrl?: string
  challenges?: string[]
  solutions?: string[]
  rating?: number
}

const isImageUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  const str = value.trim()
  if (!str) return false
  return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/')
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [imageDimensions, setImageDimensions] = useState<{[key: string]: {width: number, height: number}}>({})
  const [project, setProject] = useState<ApiProject | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    const fetchProject = async () => {
      if (!id) {
        setProject(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)
      try {
        const res = await fetch(`${API_BASE}/api/v1/projects/${encodeURIComponent(id)}`)
        if (!res.ok) {
          throw new Error(`Project API request failed: ${res.status} ${res.statusText}`)
        }
        const data = await res.json()
        const raw = (data?.data ?? data) as Record<string, unknown>

        const normalized: ApiProject = {
          id: typeof raw.id === 'string' ? raw.id : id,
          title: typeof raw.title === 'string' ? raw.title : 'Untitled Project',
          category: typeof raw.category === 'string' ? raw.category : undefined,
          description: typeof raw.description === 'string' ? raw.description : '',
          image: typeof raw.image === 'string' ? raw.image : '📱',
          technologies: Array.isArray(raw.technologies) ? raw.technologies.filter((t): t is string => typeof t === 'string') : [],
          features: Array.isArray(raw.features) ? raw.features.filter((f): f is string => typeof f === 'string') : [],
          status: typeof raw.status === 'string' ? raw.status : undefined,
          duration: typeof raw.duration === 'string' ? raw.duration : undefined,
          team: typeof raw.team === 'string' ? raw.team : undefined,
          results: Array.isArray(raw.results) ? raw.results.filter((r): r is string => typeof r === 'string') : undefined,
          gallery: Array.isArray(raw.gallery) ? raw.gallery.filter((g): g is string => typeof g === 'string') : undefined,
          liveUrl: typeof raw.liveUrl === 'string' ? raw.liveUrl : undefined,
          githubUrl: typeof raw.githubUrl === 'string' ? raw.githubUrl : undefined,
          challenges: Array.isArray(raw.challenges) ? raw.challenges.filter((c): c is string => typeof c === 'string') : undefined,
          solutions: Array.isArray(raw.solutions) ? raw.solutions.filter((s): s is string => typeof s === 'string') : undefined,
          rating: typeof raw.rating === 'number' ? raw.rating : undefined,
        }

        if (isMounted) {
          setProject(normalized)
        }
      } catch (e) {
        if (isMounted) {
          setProject(null)
          setError(e instanceof Error ? e.message : 'Failed to load project')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchProject()
    return () => {
      isMounted = false
    }
  }, [id])

  const handleImageLoad = (imageUrl: string, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement
    setImageDimensions(prev => ({
      ...prev,
      [imageUrl]: {
        width: img.naturalWidth,
        height: img.naturalHeight
      }
    }))
  }

  const getImageAspectRatio = (imageUrl: string) => {
    const dimensions = imageDimensions[imageUrl]
    if (!dimensions) return 'aspect-video'
    const aspectRatio = dimensions.width / dimensions.height
    if (aspectRatio > 1.5) return 'aspect-video'
    else if (aspectRatio < 0.8) return 'aspect-[9/16]'
    else return 'aspect-square'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center p-8">
          <p className="text-sm font-mono uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center max-w-md mx-auto p-8 rounded-card border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <h1 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
            Project Not Found
          </h1>
          <p className="text-sm mb-8 leading-relaxed" style={{ color: 'var(--muted)' }}>
            {error ? error : "Sorry, we couldn't find the project you're looking for."}
          </p>
          <Link to="/projects" className="btn-pill btn-primary no-underline inline-block">
            ← Back to Projects
          </Link>
        </div>
      </div>
    )
  }

  const openLightbox = (imageUrl: string) => {
    setSelectedImage(imageUrl)
  }

  const closeLightbox = () => {
    setSelectedImage(null)
  }

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!selectedImage || !project.gallery) return
    const currentIndex = project.gallery.findIndex(img => img === selectedImage)
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % project.gallery.length
      : (currentIndex - 1 + project.gallery.length) % project.gallery.length
    setSelectedImage(project.gallery[newIndex])
  }

  return (
    <div className="min-h-screen pt-32 pb-16" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Helmet>
        <title>{project.title} | Ionic Errrrs Code</title>
        <meta name="description" content={project.description.substring(0, 155)} />
        <link rel="canonical" href={`https://ionicerrrrscode.com/projects/${project.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Ionic Errrrs Code" />
        <meta property="og:title" content={`${project.title} | Ionic Errrrs Code`} />
        <meta property="og:description" content={project.description.substring(0, 155)} />
        <meta property="og:url" content={`https://ionicerrrrscode.com/projects/${project.id}`} />
        {project.image && <meta property="og:image" content={project.image} />}
      </Helmet>

      <div className="container-page max-w-5xl px-4">
        {/* Back Button */}
        <div className="mb-10">
          <Link 
            to="/projects" 
            className="inline-flex items-center text-sm font-semibold no-underline hover:underline"
            style={{ color: 'var(--text)' }}
          >
            ← Back to Projects
          </Link>
        </div>

        {/* Hero Header Section */}
        <div className="flex flex-col lg:flex-row items-center gap-12 pb-12 border-b mb-12" style={{ borderColor: 'var(--border)' }}>
          <div
            className="w-32 h-32 lg:w-40 lg:h-40 rounded-2xl flex-shrink-0 flex items-center justify-center border text-6xl"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
          >
            {isImageUrl(project.image) ? (
              <img 
                src={project.image} 
                alt={project.title}
                className="w-full h-full object-cover rounded-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.nextElementSibling?.classList.remove('hidden')
                }}
              />
            ) : null}
            <span className={`${isImageUrl(project.image) ? 'hidden' : ''}`}>
              {project.image}
            </span>
          </div>

          <div className="flex-1 text-center lg:text-left">
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-4">
              <span className="badge">
                <span className="badge-dot" style={{ background: project.status === 'Completed' ? 'var(--accent)' : '#eab308' }} />
                {project.status ?? 'Active'}
              </span>
              {project.category && (
                <span className="badge">
                  {project.category}
                </span>
              )}
            </div>

            <h1 className="font-display text-4xl lg:text-5xl font-bold mb-4" style={{ letterSpacing: '-0.02em' }}>
              {project.title}
            </h1>
            
            <p className="text-[1.12rem] leading-relaxed mb-6 max-w-2xl mx-auto lg:mx-0" style={{ color: 'var(--muted)' }}>
              {project.description}
            </p>

            {/* Quick stats list */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-xs font-mono" style={{ color: 'var(--muted)' }}>
              <span>⏱️ {project.duration || 'Timeline TBD'}</span>
              <span>👥 {project.team || 'Team TBD'}</span>
              {project.rating && <span>⭐ {project.rating.toFixed(1)}/5 Rating</span>}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b mb-12 flex overflow-x-auto scrollbar-hide gap-8" style={{ borderColor: 'var(--border)' }}>
          {['overview', 'gallery', 'details', 'results'].map((tab) => {
            const isActive = activeTab === tab
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="pb-4 font-semibold text-xs uppercase tracking-wider whitespace-nowrap transition-all duration-300"
                style={{
                  color: isActive ? 'var(--text)' : 'var(--muted)',
                  borderBottom: isActive ? '2px solid var(--text)' : '2px solid transparent',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 0,
                  paddingLeft: 0,
                  paddingRight: 0
                }}
              >
                {tab}
              </button>
            )
          })}
        </div>

        {/* Tab Contents */}
        <div>
          {activeTab === 'overview' && (
            <div className="space-y-12">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Tech Column */}
                <div className="lg:col-span-1">
                  <div className="rounded-card border p-8 h-full" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                    <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                      Technologies
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span key={tech} className="tag-chip">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Features Column */}
                <div className="lg:col-span-2">
                  <div className="rounded-card border p-8 h-full" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                    <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                      Key Features
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {project.features.map((feature) => (
                        <div
                          key={feature}
                          className="p-4 rounded-xl border flex items-start gap-3"
                          style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                        >
                          <span style={{ color: 'var(--border-strong)', fontWeight: 'bold' }}>◦</span>
                          <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Text overview & Solutions */}
              <div className="rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                  Project Overview
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--muted)' }}>
                  {project.description}
                </p>
                
                {project.solutions && project.solutions.length > 0 && (
                  <div className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--border)' }}>
                    <h4 className="font-display text-[1.12rem] font-bold mb-4" style={{ color: 'var(--text)' }}>
                      Solutions Implemented
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {project.solutions.map((solution) => (
                        <div
                          key={solution}
                          className="p-4 rounded-xl border flex items-start gap-3"
                          style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                        >
                          <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                            {solution}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* External links */}
                <div className="flex flex-wrap gap-4 mt-8">
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-pill btn-primary no-underline"
                    >
                      View Live Demo
                    </a>
                  )}
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-pill btn-ghost no-underline"
                    >
                      View Source Code
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
              <h3 className="font-display text-xl font-bold mb-8 text-center" style={{ color: 'var(--text)' }}>
                Project Gallery
              </h3>
              
              {/* Screenshots Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {project.gallery?.map((imageUrl, index) => (
                  <div
                    key={index}
                    className="group cursor-pointer rounded-2xl overflow-hidden border transition-all duration-300"
                    style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}
                    onClick={() => openLightbox(imageUrl)}
                  >
                    <div className={`relative ${getImageAspectRatio(imageUrl)} overflow-hidden`}>
                      <img 
                        src={imageUrl} 
                        alt={`${project.title} screenshot ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onLoad={(e) => handleImageLoad(imageUrl, e)}
                      />
                      <div 
                        className="absolute inset-0 transition-opacity opacity-0 group-hover:opacity-100 flex items-center justify-center text-sm font-semibold"
                        style={{ background: 'rgba(0,0,0,0.3)', color: '#ffffff' }}
                      >
                        Click to Expand
                      </div>
                    </div>
                  </div>
                )) || (
                  <div className="col-span-full text-center py-16">
                    <p style={{ color: 'var(--muted)' }}>
                      No gallery images available for this project.
                    </p>
                  </div>
                )}
              </div>

              {/* Gallery stats summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t pt-8" style={{ borderColor: 'var(--border)' }}>
                <div className="text-center p-6 rounded-xl" style={{ background: 'var(--surface-2)' }}>
                  <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text)' }}>
                    {project.gallery?.length || 0}
                  </div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Screenshots</div>
                </div>
                <div className="text-center p-6 rounded-xl" style={{ background: 'var(--surface-2)' }}>
                  <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text)' }}>
                    98%
                  </div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Performance Score</div>
                </div>
                <div className="text-center p-6 rounded-xl" style={{ background: 'var(--surface-2)' }}>
                  <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text)' }}>
                    {project.rating ? `${project.rating.toFixed(1)}/5` : '—'}
                  </div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--muted)' }}>User Rating</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-8">
              {/* Technical Challenges */}
              <div className="rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                  Technical Challenges
                </h3>
                <div className="space-y-4">
                  {project.challenges?.map((challenge) => (
                    <div
                      key={challenge}
                      className="p-4 rounded-xl border flex items-start gap-3"
                      style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                    >
                      <span style={{ color: '#ef4444', fontWeight: 'bold' }}>!</span>
                      <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{challenge}</span>
                    </div>
                  )) || (
                    <div className="text-center py-8">
                      <p style={{ color: 'var(--muted)' }}>No challenges listed for this project.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Timeline */}
              <div className="rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                  Project Timeline
                </h3>
                <div className="space-y-6">
                  {['Planning & Design', 'Development Phase 1', 'Development Phase 2', 'Testing & Deployment'].map((phase, index) => (
                    <div
                      key={phase}
                      className="flex items-center gap-6"
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}
                      >
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-display text-sm font-semibold mb-1.5" style={{ color: 'var(--text)' }}>{phase}</h4>
                        <div 
                          className="w-full h-1.5 rounded-full"
                          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', overflow: 'hidden' }}
                        >
                          <div 
                            className="h-full rounded-full"
                            style={{ width: `${(index + 1) * 25}%`, background: 'var(--accent)' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'results' && (
            <div className="space-y-12">
              {/* Results cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {project.results?.map((result) => (
                  <div
                    key={result}
                    className="card-studio p-8 text-center flex flex-col justify-center items-center"
                  >
                    <div className="text-3xl mb-4">◦</div>
                    <h4 className="font-display text-[1.12rem] font-bold" style={{ color: 'var(--text)' }}>{result}</h4>
                  </div>
                )) || (
                  <div className="col-span-full text-center py-16">
                    <p style={{ color: 'var(--muted)' }}>
                      No results data available for this project.
                    </p>
                  </div>
                )}
              </div>

              {/* CTA Closing */}
              <div className="text-center mt-12">
                <div className="cta-block" style={{ maxWidth: '700px', margin: '0 auto' }}>
                  <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--inverse-text)' }}>
                    Ready to Start Your Own Project?
                  </h3>
                  <p className="mt-3 text-[1.06rem]" style={{ color: 'var(--inverse-text)', opacity: 0.6, maxWidth: '500px', margin: '12px auto 0' }}>
                    Let&apos;s discuss how we can bring your vision to life with our expertise and innovative approach.
                  </p>
                  <div className="mt-8 flex justify-center gap-4">
                    <Link to="/start-project" className="btn-pill btn-inverse no-underline">
                      Start Your Project
                    </Link>
                    <Link to="/projects" className="btn-pill btn-ghost no-underline" style={{ borderColor: 'var(--inverse-text)', color: 'var(--inverse-text)' }}>
                      All Projects
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.9)' }}
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-[95vw] max-h-[95vh] flex flex-col items-center justify-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-2xl overflow-hidden border shadow-2xl" style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
              <img 
                src={selectedImage} 
                alt="Expanded screenshot"
                className="max-w-full max-h-[75vh] w-auto h-auto object-contain"
                onLoad={(e) => handleImageLoad(selectedImage, e)}
              />
            </div>
            
            {project.gallery && project.gallery.length > 1 && (
              <div className="flex items-center gap-4 text-xs font-mono" style={{ color: '#ffffff' }}>
                <button
                  onClick={() => navigateImage('prev')}
                  className="px-4 py-2 border rounded-full text-white bg-transparent hover:bg-white/10 cursor-pointer"
                  style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                >
                  Previous
                </button>
                <span>
                  {project.gallery.findIndex(img => img === selectedImage) + 1} of {project.gallery.length}
                </span>
                <button
                  onClick={() => navigateImage('next')}
                  className="px-4 py-2 border rounded-full text-white bg-transparent hover:bg-white/10 cursor-pointer"
                  style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                >
                  Next
                </button>
              </div>
            )}
            
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 bg-transparent text-white border-none cursor-pointer text-2xl font-bold"
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
